import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccessDenied, Button } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { PeriodExpensesSection } from '@/presentation/components/billing/PeriodExpensesSection';
import { formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';
import { collectionPeriod } from '@/presentation/pages/FinanceSummaryPage';

const pad = (n: number) => String(n).padStart(2, '0');

function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
}

/**
 * Finanzas → Gastos del período. Se cargan y aprueban desde el período en cobranza en adelante;
 * los anteriores se ven en solo lectura (y no generan gastos recurrentes al consultarlos).
 */
export const ExpensesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const enCobranza = useMemo(() => collectionPeriod(), []);
  const maxPeriod = useMemo(() => currentMonth(), []);

  const options = useMemo(() => {
    const list: string[] = [];
    for (let i = 0; i < 24; i += 1) list.push(shiftPeriod(maxPeriod, -i));
    return list;
  }, [maxPeriod]);

  const requested = searchParams.get('periodo') ?? '';
  const periodo = options.includes(requested) ? requested : enCobranza;
  const readOnly = periodo < enCobranza;

  const setPeriodo = (next: string) => {
    if (!options.includes(next)) return;
    const params = new URLSearchParams(searchParams);
    if (next === enCobranza) params.delete('periodo');
    else params.set('periodo', next);
    setSearchParams(params, { replace: true });
  };

  const label = (p: string) => {
    const base = formatPeriodLabel(p, i18n.language);
    if (p === enCobranza) return `${base} · ${t('pages.financeSummary.inCollection')}`;
    if (p === maxPeriod) return `${base} · ${t('pages.expensesPage.currentMonth')}`;
    return base;
  };

  if (!canAdminister) return <AccessDenied />;

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.expensesPage.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t('pages.expensesPage.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={t('pages.clinicInvoices.periodPrev')}
            disabled={periodo <= options[options.length - 1]}
            onClick={() => setPeriodo(shiftPeriod(periodo, -1))}
          >
            ‹
          </Button>
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            aria-label={t('pages.financeSummary.period')}
            className="h-9 min-w-[15rem] rounded-md border border-gray-300 bg-white px-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {options.map((p) => (
              <option key={p} value={p}>
                {label(p)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={t('pages.clinicInvoices.periodNext')}
            disabled={periodo >= maxPeriod}
            onClick={() => setPeriodo(shiftPeriod(periodo, 1))}
          >
            ›
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {readOnly ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
            {t('pages.expensesPage.readOnlyHint', { period: formatPeriodLabel(enCobranza, i18n.language) })}
          </p>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold">
          <Link
            to={`/finanzas/resumen${periodo === enCobranza ? '' : `?periodo=${periodo}`}`}
            className="text-teal-700 hover:underline dark:text-teal-300"
          >
            {t('pages.expensesPage.viewSummary')} →
          </Link>
          <Link to="/categorias-gastos" className="text-teal-700 hover:underline dark:text-teal-300">
            {t('menu.expenseCategories')} →
          </Link>
        </div>
      </div>

      <PeriodExpensesSection key={periodo} periodo={periodo} canEdit readOnly={readOnly} hideHeader />
    </div>
  );
};
